import { Link } from 'react-router-dom'
import { Sparkles, ArrowLeft } from 'lucide-react'

export default function PrivacyPage() {
    return (
        <div className="min-h-screen bg-slate-950">
            {/* Header */}
            <header className="border-b border-white/10">
                <div className="section-container py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-bold text-white">InkSpot</span>
                    </Link>
                    <Link to="/" className="btn-ghost flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="section-container py-12 max-w-4xl">
                <h1 className="text-3xl font-bold text-white mb-8">Privacy Policy</h1>
                <p className="text-white/60 mb-8">Last updated: January 4, 2026</p>

                <div className="prose prose-invert max-w-none space-y-8">
                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
                        <p className="text-white/70 leading-relaxed">
                            InkSpot ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website at inkspot.icu and our Chrome extension.
                        </p>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
                        <div className="space-y-4 text-white/70 leading-relaxed">
                            <div>
                                <h3 className="text-white font-medium mb-2">Account Information</h3>
                                <p>When you create an account, we collect your email address for authentication purposes.</p>
                            </div>
                            <div>
                                <h3 className="text-white font-medium mb-2">Text Content (Temporary Processing)</h3>
                                <p>When you use our cliché detection features, your text is processed in real-time to identify clichés and provide suggestions. <strong>We do not store your text content.</strong> Text is processed transiently and immediately discarded after analysis.</p>
                            </div>
                            <div>
                                <h3 className="text-white font-medium mb-2">Usage Analytics</h3>
                                <p>We collect anonymous usage statistics such as number of words analyzed and clichés detected to improve our service and show you your writing progress.</p>
                            </div>
                            <div>
                                <h3 className="text-white font-medium mb-2">Local Storage (Chrome Extension)</h3>
                                <p>The Chrome extension stores your preferences and settings locally in your browser using Chrome's storage API. This data never leaves your device.</p>
                            </div>
                        </div>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
                        <ul className="list-disc list-inside text-white/70 space-y-2">
                            <li>To provide and maintain our cliché detection service</li>
                            <li>To authenticate your account and manage subscriptions</li>
                            <li>To display your writing improvement statistics</li>
                            <li>To send important service-related communications</li>
                            <li>To improve our AI detection algorithms (using aggregated, anonymous data only)</li>
                        </ul>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">4. Chrome Extension Permissions</h2>
                        <div className="space-y-4 text-white/70 leading-relaxed">
                            <div>
                                <h3 className="text-white font-medium mb-2">activeTab</h3>
                                <p>Used to access the current tab's content only when you actively use the extension. This allows us to detect clichés in your text input fields.</p>
                            </div>
                            <div>
                                <h3 className="text-white font-medium mb-2">storage</h3>
                                <p>Used to save your extension preferences and settings locally in your browser.</p>
                            </div>
                            <div>
                                <h3 className="text-white font-medium mb-2">Host Permissions (all_urls)</h3>
                                <p>Required to enable cliché detection on any website where you write, including Gmail, Google Docs, social media, and other text editors.</p>
                            </div>
                        </div>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">5. Data Security</h2>
                        <p className="text-white/70 leading-relaxed">
                            We implement industry-standard security measures to protect your information. All data transmission is encrypted using HTTPS. We do not sell, trade, or otherwise transfer your personal information to third parties.
                        </p>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">6. Third-Party Services</h2>
                        <ul className="list-disc list-inside text-white/70 space-y-2">
                            <li><strong>Supabase:</strong> For secure authentication and database services</li>
                            <li><strong>Google Gemini AI:</strong> For AI-powered cliché detection (text is processed but not stored)</li>
                            <li><strong>Stripe:</strong> For secure payment processing (we don't store your payment details)</li>
                            <li><strong>Vercel:</strong> For website hosting and analytics</li>
                        </ul>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">7. Your Rights</h2>
                        <p className="text-white/70 leading-relaxed mb-4">You have the right to:</p>
                        <ul className="list-disc list-inside text-white/70 space-y-2">
                            <li>Access and receive a copy of your personal data</li>
                            <li>Request correction of inaccurate data</li>
                            <li>Request deletion of your account and associated data</li>
                            <li>Opt-out of marketing communications</li>
                            <li>Withdraw consent at any time</li>
                        </ul>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">8. Data Retention</h2>
                        <p className="text-white/70 leading-relaxed">
                            We retain your account information for as long as your account is active. Usage analytics are retained for up to 12 months. You can request deletion of your data at any time by contacting us.
                        </p>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">9. Children's Privacy</h2>
                        <p className="text-white/70 leading-relaxed">
                            Our service is not directed to children under 13. We do not knowingly collect personal information from children under 13.
                        </p>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">10. Changes to This Policy</h2>
                        <p className="text-white/70 leading-relaxed">
                            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                        </p>
                    </section>

                    <section className="glass-card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">11. Contact Us</h2>
                        <p className="text-white/70 leading-relaxed">
                            If you have any questions about this Privacy Policy, please contact us at:
                        </p>
                        <p className="text-primary-400 mt-2">
                            <a href="mailto:support@inkspot.icu" className="hover:underline">support@inkspot.icu</a>
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/10 py-8 mt-12">
                <div className="section-container text-center text-white/40 text-sm">
                    © {new Date().getFullYear()} InkSpot. All rights reserved.
                </div>
            </footer>
        </div>
    )
}
